/**
 * Card components for the GRN Allocation Repair page.
 *
 * Extracted from ``GrnAllocationRepair.tsx`` so both files stay under the
 * project's 500-line ceiling.
 *
 *  - ``GrnCard`` is the read-only card body shown for one GRN allocation.
 *  - ``DraggableGrn`` wraps it with dnd-kit draggable + per-card quick
 *    action buttons (move-to-line-0, line dropdown, split).
 *  - ``ScheduleDropZone`` is the schedule-row drop target.
 *
 * Drag identifiers are pass-through strings (``alloc-<id>`` or
 * ``grn-<id>``) so the page can route move calls to the right backend
 * endpoint without each card knowing which one to call.
 */
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { RepairGRN, RepairSchedule } from '../../lib/api/grn-repair';

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function recomputeVerdict(
  receipt: string, schedDate: string | null,
  win: { days_early: number; days_late: number },
): string {
  if (!schedDate) return '—';
  const r = new Date(receipt + 'T00:00:00Z').getTime();
  const s = new Date(schedDate + 'T00:00:00Z').getTime();
  const diff = Math.round((r - s) / (1000 * 60 * 60 * 24));
  if (diff > win.days_late) return 'late';
  if (diff < -win.days_early) return 'early';
  return 'on_time';
}

export function verdictColor(v: string): string {
  if (v === 'late') return 'text-red-600 font-semibold';
  if (v === 'early') return 'text-amber-600';
  if (v === 'on_time') return 'text-green-600';
  return 'text-muted-foreground';
}

export function GrnCard({ grn, qtyOnSlot, schedDate, win, isDragging, isOverDelivery }: {
  grn: RepairGRN; qtyOnSlot?: string; schedDate: string | null;
  win: { days_early: number; days_late: number };
  isDragging?: boolean; isOverDelivery?: boolean;
}) {
  const verdict = recomputeVerdict(grn.receipt_date, schedDate, win);
  const diffDays = schedDate
    ? Math.round((new Date(grn.receipt_date).getTime() - new Date(schedDate).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const total = grn.quantity_received;
  const showSplit = qtyOnSlot !== undefined && qtyOnSlot !== total;
  return (
    <div className={`border rounded p-2 bg-white shadow-sm w-[200px] ${isDragging ? 'opacity-50' : ''} ${isOverDelivery ? 'border-amber-400' : ''}`}>
      <div className="text-xs font-mono text-muted-foreground">#{grn.id}{isOverDelivery && ' ⚠'}</div>
      <div className="text-sm">{fmtDate(grn.receipt_date)}</div>
      <div className="text-xs">
        qty {qtyOnSlot ?? total}
        {showSplit && <span className="text-muted-foreground"> of {total}</span>}
      </div>
      <div className={`text-xs ${verdictColor(verdict)}`}>
        {verdict} {diffDays !== null && schedDate ? `(${diffDays > 0 ? '+' : ''}${diffDays}d)` : ''}
        {isOverDelivery && <span className="text-amber-600"> · over</span>}
      </div>
    </div>
  );
}

const stopDrag = (e: React.SyntheticEvent) => { e.stopPropagation(); };

export function DraggableGrn({
  dragId, grn, qtyOnSlot, isOverDelivery, schedDate, win, schedules,
  onMove, onSplit, onDelete, currentSchedId, canSplit,
}: {
  dragId: string;
  grn: RepairGRN; qtyOnSlot?: string; isOverDelivery?: boolean;
  schedDate: string | null; win: { days_early: number; days_late: number };
  schedules: RepairSchedule[];
  onMove: (schedId: number) => void;
  onSplit?: () => void;
  onDelete?: () => void;
  currentSchedId: number | null; canSplit: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: dragId });
  const line0 = schedules.find((s) => s.is_synthetic);
  const numbered = schedules.filter((s) => !s.is_synthetic);
  return (
    <div ref={setNodeRef} className="relative group">
      <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing">
        <GrnCard grn={grn} qtyOnSlot={qtyOnSlot} schedDate={schedDate} win={win} isDragging={isDragging} isOverDelivery={isOverDelivery} />
      </div>
      <div
        className="flex flex-wrap gap-1 mt-1"
        onPointerDown={stopDrag} onMouseDown={stopDrag} onClick={stopDrag}
      >
        {line0 && currentSchedId !== line0.id && (
          <button
            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 hover:bg-slate-300"
            title="Move to line 0 (synthetic anchor)"
            onClick={() => onMove(line0.id)}
          >→ 0</button>
        )}
        <select
          className="text-[10px] px-1 py-0.5 rounded border bg-white hover:bg-slate-50 max-w-[120px]"
          value=""
          onChange={(e) => {
            const sid = Number(e.target.value);
            if (sid) onMove(sid);
            e.target.value = '';
          }}
        >
          <option value="">→ line…</option>
          {numbered.filter((s) => s.id !== currentSchedId).map((s) => (
            <option key={s.id} value={s.id}>
              line {s.schedule_line_number} / {fmtDate(s.schedule_date)}
            </option>
          ))}
        </select>
        {canSplit && onSplit && (
          <button
            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-700"
            title="Split this allocation into two pieces on the same schedule"
            onClick={onSplit}
          >Split</button>
        )}
        {onDelete && (
          <button
            className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 hover:bg-red-200 text-red-700"
            title="Delete this allocation row (the schedule's shipped_qty drops by this qty)"
            onClick={onDelete}
          >✕</button>
        )}
      </div>
    </div>
  );
}

export function ScheduleDropZone({ sched, children, isActive, onCancel, onUncancel, onEditQty }: {
  sched: RepairSchedule; children: React.ReactNode; isActive?: boolean;
  onCancel?: () => void; onUncancel?: () => void; onEditQty?: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `sched-${sched.id}` });
  const isCancelled = sched.status === 'cancelled';
  const filled = parseFloat(sched.allocated_qty);
  const cap = parseFloat(sched.schedule_qty);
  const fillState = cap === 0 ? 'empty' : filled > cap ? 'over' : filled === cap ? 'full' : filled > 0 ? 'partial' : 'empty';
  const bgClass = isCancelled ? 'bg-slate-100 opacity-70' : {
    empty: 'bg-slate-50',
    partial: 'bg-amber-50',
    full: 'bg-green-50',
    over: 'bg-red-50',
  }[fillState];
  const dropClass = isOver ? 'ring-2 ring-blue-500' : '';
  return (
    <div ref={setNodeRef} className={`border rounded-md p-3 mb-3 ${bgClass} ${dropClass} ${isActive ? 'border-blue-500' : ''}`}>
      <div className="flex justify-between items-baseline mb-2">
        <div className="flex gap-3 items-baseline">
          <div className={`font-mono text-sm ${isCancelled ? 'line-through text-muted-foreground' : ''}`}>Line {sched.schedule_line_number}</div>
          <div className="text-sm">{fmtDate(sched.schedule_date)}</div>
          <div className="text-xs text-muted-foreground">
            qty {sched.allocated_qty}/{sched.schedule_qty}
            {!isCancelled && fillState === 'over' && ' ⚠ OVER'}
            {!isCancelled && fillState === 'full' && ' ✓'}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className={`text-xs uppercase tracking-wide ${isCancelled ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>{sched.status}</div>
          {/* Edit qty + Cancel: real (numbered, dated) schedules only.
              Hidden for the synthetic line-0 bucket and cancelled rows. */}
          {onEditQty && !sched.is_synthetic && !isCancelled && (
            <button
              className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-700"
              title="Edit this schedule line's demanded quantity"
              onClick={onEditQty}
            >Edit qty</button>
          )}
          {onCancel && !sched.is_synthetic && !isCancelled && (
            <button
              className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 hover:bg-red-200 text-red-700"
              title="Cancel this schedule line and transfer its GRNs to the active schedule"
              onClick={onCancel}
            >Cancel line</button>
          )}
          {/* Un-cancel: cancelled real rows only — reactivates the retired slot. */}
          {onUncancel && !sched.is_synthetic && isCancelled && (
            <button
              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
              title="Reactivate this cancelled schedule line"
              onClick={onUncancel}
            >Un-cancel</button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[40px]">{children}</div>
    </div>
  );
}
